import { createContext, useContext, useSyncExternalStore } from 'react';
import {
  EMPTY_SUBSCRIPTION_ACTION,
  getSubscriptionAction,
  subscribeSubscriptionAction,
} from './subscriptionActionStore.js';
import type { SubscriptionActionData, SubscriptionActionStore } from './subscriptionActionStore.js';
import type { ReactNode } from 'react';

// NOTE!
// - DO NOT use the `SubscriptionActionContext` outside of this file, except for tests. It is only exported for tests.
export const SubscriptionActionContext = createContext<SubscriptionActionData>(EMPTY_SUBSCRIPTION_ACTION);

interface SubscriptionActionProviderProps {
  subscriptionActionStore?: SubscriptionActionStore;
  children?: ReactNode;
}

/**
 * - Client-side: omit `subscriptionActionStore` — uses the default module-level singleton (safe: one user per browser tab).
 * - SSR: pass a fresh `createSubscriptionActionStore()` instance per request to avoid cross-request leakage. The server
 *   snapshot is always `EMPTY_SUBSCRIPTION_ACTION`, so the transient duplicate-contact state is never part of SSR HTML.
 */
export const SubscriptionActionProvider = ({ subscriptionActionStore, children }: SubscriptionActionProviderProps) => {
  const subscribe = subscriptionActionStore?.subscribe ?? subscribeSubscriptionAction;
  const getSnapshot = subscriptionActionStore?.getSubscriptionAction ?? getSubscriptionAction;
  const data = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_SUBSCRIPTION_ACTION);
  return <SubscriptionActionContext.Provider value={data}>{children}</SubscriptionActionContext.Provider>;
};

export const useSubscriptionAction = (): SubscriptionActionData => useContext(SubscriptionActionContext);
